import type { UnitAvailability } from '@/api/accommodation/types'
import { addDays, compareAsc, differenceInCalendarDays, isSameDay, isValid, max, parseISO, startOfDay } from 'date-fns'

export interface DateSelectionWindow {
  firstDay: Date
  lastDay: Date
  days: number
}

/**
 * Derives the location-specific booking window from actual departure dates
 * returned by the API. Standalone terminal records are intentionally ignored:
 * a date is bookable only when an arrival references it as a departure.
 */
export function getDateSelectionWindow(
  availability: UnitAvailability,
  today: Date = new Date(),
): DateSelectionWindow | null {
  const departureDates = availability
    .flatMap((day) => day.departures ?? [])
    .map((departure) => parseISO(departure))
    .filter(isValid)

  if (departureDates.length === 0) return null

  const firstDay = startOfDay(today)
  const lastDay = startOfDay(max(departureDates))
  const days = differenceInCalendarDays(lastDay, firstDay)

  if (days <= 0) return null

  return { firstDay, lastDay, days }
}

/**
 * Departures come from the API as-is: they already respect the minimum and
 * maximum stay, closed-to-departure days and the offer's terminal departure.
 */
export function getSelectableDepartureDates(
  availability: UnitAvailability,
  arrivalDate: Date,
  firstDay: Date,
  lastDay: Date,
): Date[] {
  const arrival = availability.find((day) => isSameDay(day.date, arrivalDate))

  if (!arrival?.departures) return []

  return arrival.departures
    .map((departure) => parseISO(departure))
    .filter((departure) => isValid(departure) && startOfDay(departure) >= firstDay && startOfDay(departure) <= lastDay)
    .sort(compareAsc)
}

/**
 * Whether the free nights following an arrival cover its minimum stay. When
 * they do and the arrival still has no departure, the departure rules
 * (closed-to-departure days, maximum stay) are what rule it out.
 */
export function coversMinimumNights(availability: UnitAvailability, arrival: UnitAvailability[number]): boolean {
  const nightDates = Array.from({ length: arrival.minNights }, (_, night) => addDays(arrival.date, night))
  const departureDate = addDays(arrival.date, arrival.minNights)

  return (
    nightDates.every((nightDate) =>
      availability.some((day) => day.state === 'arrival' && isSameDay(day.date, nightDate)),
    ) && availability.some((day) => isSameDay(day.date, departureDate))
  )
}
